/* MY BABY: browser-side collection filters for English and Arabic.

   Shopify keeps one filter-value list per language and rebuilds it whenever a
   product changes. On this store the Hebrew list is current; the English and
   Arabic lists have been frozen since March 2026 and only Shopify can restart
   them. So on secondary locales sections/collection.liquid renders every
   product of the collection (paginate by 250) with its facet values on the
   card (snippets/mb-facet-card-attrs.liquid), snippets/mb-facets-sidebar.liquid
   lists the values those cards carry, and this file does the selecting:
   AND across groups, OR within a group, counts that follow the selection,
   the URL kept in sync (mb.* parameters, never filter.*, which would hand the
   request back to the frozen server index), paging in the browser, pills,
   badges, the empty state.

   Sorting stays with the theme: a sort click still goes through
   <collection-filters-form>.submitForm (mb-collection-filters.js fetches the
   section sorted, keeps mb.* in the address) and this file re-applies the
   selection when the grid comes back (mb:filters:updated).

   Load AFTER collection-filters-form.js and mb-collection-filters.js. */

(function () {
  'use strict';

  var NAME = 'collection-filters-form';
  var CONTAINER = '[data-section-id][data-facet-mode="client"]';
  var GRID = '[data-products-grid]';
  var CARD = '[data-mb-card]';
  var FORM = '[data-mb-facets-form]';
  var SIDEBAR = '[data-mb-sidebar]';
  var COUNT = '[data-results-count]';
  var BADGE = '[data-active-filters]';
  var GROUP = '[data-mb-group]';
  var GROUP_COUNT = '[data-mb-group-count]';
  var VALUE_ROW = '[data-mb-value-row]';
  var VALUE_COUNT = '[data-mb-count]';
  var ACTIVE = '[data-mb-active]';
  var PILLS = '[data-mb-pills]';
  var PILL_TEMPLATE = '[data-mb-pill-template]';
  var CLEAR = '[data-mb-clear]';
  var RESET = '[data-mb-reset]';
  var EMPTY = '[data-mb-empty]';
  var MORE_WRAP = '[data-mb-more-wrap]';
  var MORE = '[data-mb-more]';
  var SHOWING = '[data-mb-showing]';
  var VIEW_COUNT = '[data-mb-view-count]';
  var SLIDER = 'range-slider[data-range-slider]';
  var PRICE_MIN = '[data-field-price-min]';
  var PRICE_MAX = '[data-field-price-max]';
  var PARAM = 'mb.';
  var SEP = '||';
  var LIST_KEYS = ['avail', 'color', 'vendor', 'type', 'age', 'gender', 'sale', 'install', 'feat'];
  var ATTR = {
    avail: 'data-mb-avail', color: 'data-mb-color', vendor: 'data-mb-vendor', type: 'data-mb-type',
    age: 'data-mb-age', gender: 'data-mb-gender', sale: 'data-mb-sale', install: 'data-mb-install', feat: 'data-mb-feat'
  };
  /* Longer than the theme's own 500 ms price handler on the same inputs, so
     ours runs after it and can normalise whatever it wrote back. */
  var PRICE_DEBOUNCE = 600;

  var instances = [];

  function toArray(list) { return Array.prototype.slice.call(list || []); }

  function splitValues(raw) {
    if (!raw) return [];
    return raw.split(SEP).filter(function (v) { return v !== ''; });
  }

  function readCards(grid) {
    return toArray(grid.querySelectorAll(CARD)).map(function (el) {
      var facets = {};
      LIST_KEYS.forEach(function (key) {
        facets[key] = splitValues(el.getAttribute(ATTR[key]));
      });
      var price = parseFloat(el.getAttribute('data-mb-price'));
      return { el: el, facets: facets, price: isNaN(price) ? null : price };
    });
  }

  /* ---- URL state -------------------------------------------------------- */

  function readState(priceBounds) {
    var params = new window.URL(window.location.href).searchParams;
    var selected = {};
    LIST_KEYS.forEach(function (key) {
      var values = params.getAll(PARAM + key).filter(function (value, i, all) {
        return value && all.indexOf(value) === i;
      });
      if (values.length) selected[key] = values;
    });
    var gte = parseFloat(params.get(PARAM + 'price.gte'));
    var lte = parseFloat(params.get(PARAM + 'price.lte'));
    var price = null;
    if (priceBounds && (!isNaN(gte) || !isNaN(lte))) {
      price = {
        gte: isNaN(gte) ? priceBounds.min : Math.max(priceBounds.min, gte),
        lte: isNaN(lte) ? priceBounds.max : Math.min(priceBounds.max, lte)
      };
      if (price.gte <= priceBounds.min && price.lte >= priceBounds.max) price = null;
    }
    return { selected: selected, price: price };
  }

  function writeState(state, replace) {
    var url = new window.URL(window.location.href);
    var params = url.searchParams;
    /* params.keys() is an iterator, not array-like: collect it first, then
       delete, or nothing is removed and every click appends a duplicate. */
    var keys = [];
    params.forEach(function (value, key) { if (keys.indexOf(key) < 0) keys.push(key); });
    keys.forEach(function (key) {
      if (key.indexOf(PARAM) === 0 || key === 'page') params.delete(key);
    });
    LIST_KEYS.forEach(function (key) {
      (state.selected[key] || []).forEach(function (value) { params.append(PARAM + key, value); });
    });
    if (state.price) {
      params.set(PARAM + 'price.gte', String(state.price.gte));
      params.set(PARAM + 'price.lte', String(state.price.lte));
    }
    var query = params.toString();
    var next = url.pathname + (query ? '?' + query : '') + url.hash;
    var current = window.location.pathname + window.location.search + window.location.hash;
    if (next === current) return;
    if (replace) window.history.replaceState(null, '', next);
    else window.history.pushState(null, '', next);
  }

  /* ---- matching ---------------------------------------------------------- */

  function matchesList(card, key, values) {
    var own = card.facets[key];
    for (var i = 0; i < values.length; i++) {
      if (own.indexOf(values[i]) > -1) return true;
    }
    return false;
  }

  function matchesPrice(card, price) {
    if (!price) return true;
    if (card.price === null) return false;
    return card.price >= price.gte && card.price <= price.lte;
  }

  /* Every group except `skip` must be satisfied: AND across groups, OR inside. */
  function matches(card, state, skip) {
    var keys = Object.keys(state.selected);
    for (var i = 0; i < keys.length; i++) {
      if (keys[i] === skip) continue;
      if (!matchesList(card, keys[i], state.selected[keys[i]])) return false;
    }
    if (skip !== 'price' && !matchesPrice(card, state.price)) return false;
    return true;
  }

  /* ---- one collection section -------------------------------------------- */

  function Facets(container) {
    this.container = container;
    this.pageSize = parseInt(container.getAttribute('data-mb-page-size'), 10) || 24;
    this.shown = this.pageSize;
    this.priceTimer = null;
    this.bindContainer();
    this.bind();
  }

  /* The container outlives every sort re-render (only the grid and the sidebar
     are swapped), so its listeners are attached exactly once. Binding them in
     bind() stacked a handler per re-render and "show more" started jumping by
     two pages after the first sort. */
  Facets.prototype.bindContainer = function () {
    var self = this;
    this.container.addEventListener('click', function (event) {
      var pill = event.target.closest ? event.target.closest('[data-mb-pill]') : null;
      if (pill) {
        event.preventDefault();
        self.remove(pill.getAttribute('data-mb-key'), pill.getAttribute('data-mb-value'));
        return;
      }
      if (event.target.closest && (event.target.closest(CLEAR) || event.target.closest(RESET))) {
        event.preventDefault();
        self.clearAll();
        return;
      }
      if (event.target.closest && event.target.closest(MORE)) {
        event.preventDefault();
        var firstNew = self.shown;
        self.shown += self.pageSize;
        self.apply(false);
        /* On the last batch the button disappears under the keyboard user;
           move focus to the first card just revealed instead of <body>. */
        var wrap = self.container.querySelector(MORE_WRAP);
        if (wrap && wrap.hasAttribute('hidden')) {
          var shownCards = self.cards.filter(function (card) { return !card.el.hasAttribute('hidden'); });
          var target = shownCards[firstNew] || shownCards[shownCards.length - 1];
          var link = target ? target.el.querySelector('a[href]') : null;
          if (link) link.focus();
        }
      }
    });
  };

  /* Everything below hangs off the form or the grid, both of which are replaced
     by a sort re-render, so it runs again on every mb:filters:updated. */
  Facets.prototype.bind = function () {
    var self = this;
    this.grid = this.container.querySelector(GRID);
    this.form = this.container.querySelector(FORM);
    this.sidebar = this.container.querySelector(SIDEBAR);
    if (!this.grid || !this.form) return;

    this.cards = readCards(this.grid);
    /* From here on the script owns visibility through the hidden attribute. */
    this.cards.forEach(function (card) { card.el.removeAttribute('data-mb-overflow'); });
    this.priceBounds = this.readPriceBounds();
    this.state = readState(this.priceBounds);
    this.syncInputs();
    this.shown = this.pageSize;

    this.form.addEventListener('change', function (event) {
      var input = event.target;
      if (!input || input.type !== 'checkbox' || input.name.indexOf(PARAM) !== 0) return;
      self.readSelection();
      self.commit();
    });

    this.form.addEventListener('input', function (event) {
      var input = event.target;
      if (!input || (!input.hasAttribute('data-field-price-min') && !input.hasAttribute('data-field-price-max'))) return;
      if (self.priceTimer) clearTimeout(self.priceTimer);
      self.priceTimer = setTimeout(function () {
        self.priceTimer = null;
        self.readPriceFromInputs();
        self.pushInputsFromState();
        self.pushSliderFromState();
        self.commit();
      }, PRICE_DEBOUNCE);
    });

    this.form.addEventListener('theme:range:update', function (event) {
      var slider = event.target && event.target.closest ? event.target.closest(SLIDER) : null;
      if (!slider) slider = self.form.querySelector(SLIDER);
      if (!slider) return;
      self.readPriceFromSlider(slider);
      self.pushInputsFromState();
      self.commit();
    });

    this.apply(false);
  };

  Facets.prototype.readPriceBounds = function () {
    var slider = this.form.querySelector(SLIDER);
    if (!slider) return null;
    var min = parseFloat(slider.getAttribute('data-se-min'));
    var max = parseFloat(slider.getAttribute('data-se-max'));
    if (isNaN(min) || isNaN(max)) return null;
    return { min: min, max: max };
  };

  Facets.prototype.readSelection = function () {
    var selected = {};
    toArray(this.form.querySelectorAll('input[type="checkbox"][name^="' + PARAM + '"]:checked')).forEach(function (input) {
      var key = input.name.slice(PARAM.length);
      (selected[key] = selected[key] || []).push(input.value);
    });
    this.state.selected = selected;
    this.shown = this.pageSize;
  };

  Facets.prototype.readPriceFromInputs = function () {
    if (!this.priceBounds) return;
    var minEl = this.form.querySelector(PRICE_MIN);
    var maxEl = this.form.querySelector(PRICE_MAX);
    var gte = minEl && minEl.value !== '' ? parseFloat(minEl.value) : this.priceBounds.min;
    var lte = maxEl && maxEl.value !== '' ? parseFloat(maxEl.value) : this.priceBounds.max;
    if (isNaN(gte)) gte = this.priceBounds.min;
    if (isNaN(lte)) lte = this.priceBounds.max;
    gte = Math.max(this.priceBounds.min, gte);
    lte = Math.min(this.priceBounds.max, lte);
    if (gte > lte) { var t = gte; gte = lte; lte = t; }
    this.state.price = (gte <= this.priceBounds.min && lte >= this.priceBounds.max) ? null : { gte: gte, lte: lte };
    this.shown = this.pageSize;
  };

  Facets.prototype.readPriceFromSlider = function (slider) {
    if (!this.priceBounds) return;
    var gte = parseFloat(slider.getAttribute('data-se-min-value'));
    var lte = parseFloat(slider.getAttribute('data-se-max-value'));
    if (isNaN(gte)) gte = this.priceBounds.min;
    if (isNaN(lte)) lte = this.priceBounds.max;
    this.state.price = (gte <= this.priceBounds.min && lte >= this.priceBounds.max) ? null : { gte: gte, lte: lte };
    this.shown = this.pageSize;
  };

  Facets.prototype.pushInputsFromState = function () {
    var minEl = this.form.querySelector(PRICE_MIN);
    var maxEl = this.form.querySelector(PRICE_MAX);
    if (minEl) minEl.value = this.state.price ? String(this.state.price.gte) : '';
    if (maxEl) maxEl.value = this.state.price ? String(this.state.price.lte) : '';
  };

  Facets.prototype.pushSliderFromState = function () {
    var slider = this.form.querySelector(SLIDER);
    if (!slider || !this.priceBounds) return;
    var gte = this.state.price ? this.state.price.gte : this.priceBounds.min;
    var lte = this.state.price ? this.state.price.lte : this.priceBounds.max;
    slider.setAttribute('data-se-min-value', String(gte));
    slider.setAttribute('data-se-max-value', String(lte));
    if (typeof slider.setMinValue === 'function' && typeof slider.setMaxValue === 'function') {
      try { slider.setMinValue(gte); slider.setMaxValue(lte); } catch (e) { /* slider not ready yet */ }
    }
  };

  /* Checkboxes and price fields follow the URL, not the other way round, so a
     shared link, a back button or a sort re-render all land on the same state. */
  Facets.prototype.syncInputs = function () {
    var state = this.state;
    toArray(this.form.querySelectorAll('input[type="checkbox"][name^="' + PARAM + '"]')).forEach(function (input) {
      var key = input.name.slice(PARAM.length);
      input.checked = !!(state.selected[key] && state.selected[key].indexOf(input.value) > -1);
    });
    this.pushInputsFromState();
    this.pushSliderFromState();
  };

  Facets.prototype.remove = function (key, value) {
    /* Remember where the keyboard was: the pill is about to be rebuilt. */
    var holder = this.container.querySelector(PILLS);
    var active = document.activeElement;
    var focusIndex = holder && active && holder.contains(active) ? toArray(holder.children).indexOf(active.closest('[data-mb-pill]')) : -1;
    if (key === 'price') {
      this.state.price = null;
    } else if (this.state.selected[key]) {
      this.state.selected[key] = this.state.selected[key].filter(function (v) { return v !== value; });
      if (!this.state.selected[key].length) delete this.state.selected[key];
    }
    this.shown = this.pageSize;
    this.syncInputs();
    this.commit();
    if (focusIndex > -1) {
      var pills = holder ? toArray(holder.children) : [];
      var next = pills[Math.min(focusIndex, pills.length - 1)] || this.container.querySelector(CLEAR + ':not([hidden])') || this.form.querySelector('input[type="checkbox"]:not(:disabled)');
      if (next) next.focus();
    }
  };

  Facets.prototype.clearAll = function () {
    this.state = { selected: {}, price: null };
    this.shown = this.pageSize;
    this.syncInputs();
    this.commit();
  };

  Facets.prototype.commit = function () {
    writeState(this.state, false);
    this.apply(true);
  };

  Facets.prototype.applyFromUrl = function () {
    this.state = readState(this.priceBounds);
    this.shown = this.pageSize;
    this.syncInputs();
    this.apply(false);
  };

  Facets.prototype.apply = function (scroll) {
    var self = this;
    var state = this.state;
    var visible = this.cards.filter(function (card) { return matches(card, state, null); });
    var total = visible.length;

    /* Cards: matching ones in DOM order up to the paging window, the rest hidden. */
    var visibleSet = visible.slice(0, this.shown);
    this.cards.forEach(function (card) {
      var show = visibleSet.indexOf(card) > -1;
      if (show) card.el.removeAttribute('hidden');
      else card.el.setAttribute('hidden', '');
    });

    /* Counts: a value's count is the number of products that satisfy every
       OTHER group's selection and carry the value, exactly what Shopify shows. */
    toArray(this.form.querySelectorAll(GROUP)).forEach(function (group) {
      var key = group.getAttribute('data-mb-group');
      if (key === 'price') {
        var priceActive = !!state.price;
        self.setGroupCount(group, priceActive ? 1 : 0);
        return;
      }
      var pool = self.cards.filter(function (card) { return matches(card, state, key); });
      var active = 0;
      toArray(group.querySelectorAll(VALUE_ROW)).forEach(function (row) {
        var input = row.querySelector('input');
        var countEl = row.querySelector(VALUE_COUNT);
        if (!input) return;
        var value = input.value;
        var count = pool.filter(function (card) { return card.facets[key].indexOf(value) > -1; }).length;
        if (countEl) countEl.textContent = String(count);
        var checked = input.checked;
        if (checked) active += 1;
        input.disabled = count === 0 && !checked;
        row.classList.toggle('sidebar__item--active', checked);
        row.classList.toggle('link--remove', checked);
        row.classList.toggle('link--disable', count === 0 && !checked);
        row.classList.toggle('link--add', count > 0 && !checked);
      });
      self.setGroupCount(group, active);
    });

    /* The server pager counts pages of the UNFILTERED collection, and its
       links carry no mb.* filter, so while a filter is on it would promise
       pages that do not exist and drop the filter on tap. Hidden until every
       filter is cleared (the load more button pages the filtered set). */
    var filtering = !!state.price || Object.keys(state.selected).some(function (key) {
      return (state.selected[key] || []).length > 0;
    });
    toArray(this.container.querySelectorAll('.pagination-custom')).forEach(function (nav) {
      nav.style.display = filtering ? 'none' : '';
    });

    this.renderPills();
    this.renderCounts(total, visibleSet.length);
    this.renderEmpty(total);
    this.grid.classList.remove('is-loading');
    this.container.classList.remove('mb-facets-pending');

    if (scroll && window.theme && typeof window.theme.scrollTo === 'function') {
      var top = this.grid.getBoundingClientRect().top;
      var mobile = window.theme && typeof window.theme.isMobile === 'function' ? window.theme.isMobile() : window.innerWidth < 750;
      var covered = mobile && this.container.querySelector('[data-collection-sidebar]') &&
        this.container.querySelector('[data-collection-sidebar]').classList.contains('expanded');
      if (top < 0 && !covered) {
        var nav = this.container.querySelector('[data-collection-nav]');
        window.theme.scrollTo(top - (nav ? nav.offsetHeight : 0));
      }
    }

    document.dispatchEvent(new CustomEvent('mb:facets:applied', { detail: { total: total } }));
  };

  Facets.prototype.setGroupCount = function (group, active) {
    var el = group.querySelector(GROUP_COUNT);
    if (!el) return;
    el.textContent = active ? '(' + active + ')' : '';
    el.toggleAttribute('hidden', !active);
  };

  Facets.prototype.labelFor = function (key, value) {
    var input = this.form.querySelector('input[name="' + PARAM + key + '"][value="' + value.replace(/"/g, '\\"') + '"]');
    var label = input && input.parentElement ? input.parentElement.querySelector('.mybaby-filter-value-label') : null;
    return label ? label.textContent.trim() : value;
  };

  Facets.prototype.renderPills = function () {
    var self = this;
    var active = this.container.querySelector(ACTIVE);
    var holder = this.container.querySelector(PILLS);
    var template = this.container.querySelector(PILL_TEMPLATE);
    var clear = this.container.querySelector(CLEAR);
    var reset = this.container.querySelector(RESET);
    var items = [];
    LIST_KEYS.forEach(function (key) {
      (self.state.selected[key] || []).forEach(function (value) {
        items.push({ key: key, value: value, label: self.labelFor(key, value) });
      });
    });
    if (this.state.price) {
      items.push({ key: 'price', value: '', label: this.money(this.state.price.gte) + ' - ' + this.money(this.state.price.lte) });
    }

    if (holder && template) {
      holder.innerHTML = '';
      items.forEach(function (item) {
        var node = template.content.firstElementChild.cloneNode(true);
        node.setAttribute('data-mb-key', item.key);
        node.setAttribute('data-mb-value', item.value);
        node.querySelector('small').textContent = item.label;
        holder.appendChild(node);
      });
    }
    if (active) active.toggleAttribute('hidden', items.length === 0);
    if (clear) clear.toggleAttribute('hidden', items.length < 2);
    if (reset) reset.toggleAttribute('hidden', items.length === 0);
    var buttons = this.container.querySelector('[data-mb-buttons]');
    if (buttons && !buttons.hasAttribute('data-mb-buttons-keep')) buttons.toggleAttribute('hidden', items.length === 0);
    if (this.sidebar) this.sidebar.classList.toggle('collection__sidebar--has-buttons', items.length > 0);

    var count = items.length;
    if (this.sidebar) this.sidebar.setAttribute('data-active-filters-count', String(count));
    toArray(this.container.querySelectorAll(BADGE)).forEach(function (badge) {
      badge.textContent = String(count);
      badge.classList.toggle('hidden', count < 1);
    });
  };

  /* Same formatting as every other price on the page, without trailing .00 */
  Facets.prototype.money = function (amount) {
    var theme = window.theme || {};
    if (typeof theme.formatMoney === 'function' && theme.moneyFormat) {
      try {
        return theme.formatMoney(Math.round(amount * 100), theme.moneyFormat).replace(/([.,]00)(?=\D|$)/, '');
      } catch (e) { /* fall through */ }
    }
    var symbol = this.sidebar ? (this.sidebar.getAttribute('data-mb-currency') || '') : '';
    return symbol + amount;
  };

  Facets.prototype.renderCounts = function (total, shownNow) {
    var countEl = this.container.querySelector(COUNT);
    if (countEl) {
      var one = countEl.getAttribute('data-mb-one') || '';
      var other = countEl.getAttribute('data-mb-other') || '';
      countEl.textContent = total + ' ' + (total === 1 ? one : other);
    }
    var view = this.container.querySelector(VIEW_COUNT);
    if (view && view.getAttribute('data-mb-template')) {
      view.textContent = view.getAttribute('data-mb-template').replace('[count]', String(total));
    }
    var wrap = this.container.querySelector(MORE_WRAP);
    var showing = this.container.querySelector(SHOWING);
    if (wrap) wrap.toggleAttribute('hidden', shownNow >= total);
    if (showing) {
      var tpl = showing.getAttribute('data-mb-template') || '';
      showing.textContent = tpl.replace('[shown]', String(shownNow)).replace('[total]', String(total));
    }
  };

  Facets.prototype.renderEmpty = function (total) {
    var empty = this.container.querySelector(EMPTY);
    if (empty) empty.toggleAttribute('hidden', total > 0);
  };

  /* ---- wiring into the theme's form element -------------------------------- */

  function instanceFor(container) {
    for (var i = 0; i < instances.length; i++) {
      if (instances[i].container === container) return instances[i];
    }
    return null;
  }

  function boot() {
    toArray(document.querySelectorAll(CONTAINER)).forEach(function (container) {
      if (instanceFor(container)) return;
      instances.push(new Facets(container));
    });
  }

  /* The theme swaps the grid and the sidebar after a sort: the new nodes carry
     no state, so read the address bar again and re-apply. */
  document.addEventListener('mb:filters:updated', function () {
    var sortNow = new window.URL(window.location.href).searchParams.get('sort_by') || '';
    instances.forEach(function (instance) {
      if (!document.body.contains(instance.container)) return;
      /* The grid now carries this sort order; the back button compares against it. */
      instance.container.setAttribute('data-mb-sort', sortNow);
      instance.bind();
    });
  });

  function patch() {
    var Klass = window.customElements && window.customElements.get(NAME);
    if (!Klass || Klass.prototype.mbLocalFacets) return;
    Klass.prototype.mbLocalFacets = true;
    var previous = Klass.prototype.submitForm;

    Klass.prototype.submitForm = function (event, href) {
      var container = this.container || this.closest('[data-section-id]');
      var instance = container && container.getAttribute('data-facet-mode') === 'client' ? instanceFor(container) : null;
      if (instance) {
        var type = event ? event.type : '';
        if (type === 'popstate') {
          /* Back/forward inside our own state: no request needed. If the sort
             changed as well, the grid order is stale and the theme must fetch. */
          var sortNow = new window.URL(window.location.href).searchParams.get('sort_by') || '';
          var sortRendered = container.getAttribute('data-mb-sort') || '';
          if (sortNow === sortRendered) {
            if (this.sort && typeof this.onSortCheck === 'function') this.onSortCheck(event);
            instance.applyFromUrl();
            return;
          }
        } else if (type === 'input' || type === 'change' || type === 'theme:range:update') {
          return;
        }
      }
      return previous.apply(this, arguments);
    };
  }

  if (window.customElements) {
    if (window.customElements.get(NAME)) patch();
    else window.customElements.whenDefined(NAME).then(patch);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
